import { Fragment, useMemo } from 'react'
import { toBuyLinkHref } from '~/src/lib/withRedirectClickSource'

export interface ProductLink {
  name: string
  url: string
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

interface CompiledMatcher {
  pattern: RegExp
  lookupByLower: Map<string, ProductLink>
}

function compileMatcher(products: ProductLink[]): CompiledMatcher | null {
  // Filter out empty names; sort longest first so prefix names don't
  // shadow more-specific ones ("Brooks Ghost 17" before "Brooks Ghost").
  const usable = products
    .map((p) => ({ name: p.name.trim(), url: p.url }))
    .filter((p) => p.name.length > 0 && p.url.length > 0)
    .sort((a, b) => b.name.length - a.name.length)
  if (usable.length === 0) return null

  const lookupByLower = new Map<string, ProductLink>()
  for (const product of usable) {
    const key = product.name.toLowerCase()
    if (!lookupByLower.has(key)) lookupByLower.set(key, product)
  }

  // Lookbehind/lookahead with a non-letter assertion so "Glycerin" won't
  // hit inside "Glycerinade", but punctuation/spaces still let it match.
  const pattern = new RegExp(
    `(?<![\\p{L}\\p{N}])(${usable.map((p) => escapeRegex(p.name)).join('|')})(?![\\p{L}\\p{N}])`,
    'giu'
  )

  return { pattern, lookupByLower }
}

/**
 * Walks plain text, replacing any product-name mention with a link to that
 * product's first purchase URL. Longer names win over shorter prefixes
 * (e.g. "Brooks Ghost 17" matches before "Brooks Ghost"). Matching is
 * case-insensitive and bounded by a non-letter on each side so it doesn't
 * partial-match inside other words.
 */
export function LinkifyProductNames({
  text,
  products,
  className = 'font-medium text-info-700 underline underline-offset-2 hover:no-underline',
}: {
  text: string
  products: ProductLink[]
  className?: string
}) {
  // Memo the compiled matcher across renders — typical reports render this
  // dozens of times (every quick-answer + ranked-product summary) with the
  // same product list, so building the regex once keeps parent re-renders
  // cheap.
  const matcher = useMemo(() => compileMatcher(products), [products])

  if (!text || matcher == null) return <>{text}</>

  const { pattern, lookupByLower } = matcher
  const segments: Array<
    | { type: 'text'; value: string }
    | { type: 'link'; match: string; url: string }
  > = []
  let lastIndex = 0

  // matchAll handles the global-regex iteration without the manual
  // lastIndex juggling the previous version needed. Our pattern always
  // captures at least one character (the non-empty alternation), so the
  // zero-length-loop guard is no longer needed.
  for (const match of text.matchAll(pattern)) {
    const matchIndex = match.index ?? 0
    if (matchIndex > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, matchIndex) })
    }
    const matched = match[0]
    const product = lookupByLower.get(matched.toLowerCase())
    if (product) {
      segments.push({ type: 'link', match: matched, url: product.url })
    } else {
      segments.push({ type: 'text', value: matched })
    }
    lastIndex = matchIndex + matched.length
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) })
  }

  return (
    <>
      {segments.map((segment, i) =>
        segment.type === 'text' ? (
          <Fragment key={i}>{segment.value}</Fragment>
        ) : (
          <a
            key={i}
            href={toBuyLinkHref(segment.url, 'seo-report')}
            target='_blank'
            rel='sponsored nofollow noopener noreferrer'
            className={className}
          >
            {segment.match}
          </a>
        )
      )}
    </>
  )
}
